import Moment from "react-moment";
import Spinner from "../../../components/spinner";
import useRead from "../../../hooks/useRead";
import { getOrderPdf, exportOrders } from "../../../apis/orders";
import { useMutation } from "@tanstack/react-query";
import { IconButton } from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { Link } from "react-router-dom";
import useCreate from "../../../hooks/useCreate";
import spinner from "../../../assets/images/common/spinner.gif";
import Pagination from "../../../components/elements/pagination";
import { useState } from "react";
import ReactDatePicker from "react-datepicker";
import moment from "moment";
import { toast } from "react-hot-toast";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../../components/ui/table";
import PrimaryButton from "../../../components/elements/buttons/PrimaryButton";

const Orders = () => {
  const initialData = {
    limit: 50,
    pageNo: 0,
    order_id: "",
    name: "",
    phone: "",
    order_date_from: "",
    order_date_to: "",
    total_sum: "",
    paymentModeFilter: "",
    delivery_status: "",
  };
  const limit = 50;
  const [searchInputs, setSearchInputs] = useState(initialData);
  const { data, paramsObject, setGetListParams } = useRead({
    method: "GET",
    initialData,
    url: "orders/list",
    keepPreviousData: true,
    onError: (err) => {
      toast.error(err.message || "Failed to fetch orders");
    },
  });
  const handleSearchVal = (e) => {
    const value = e.target.value;
    setSearchInputs({
      ...searchInputs,
      [e.target.name]: value,
    });
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      setGetListParams({
        ...paramsObject,
        ...searchInputs,
        pageNo: 0,
      });
    }
  };

  const handleDropdownChange = (e) => {
    const value = e.target.value;
    const next = { ...searchInputs, [e.target.name]: value };
    if (e.target.name === "paymentModeFilter") {
      const newState = statusList?.data?.data?.find((s) => String(s.status_id) === String(value));
      if (newState && ["Canceled", "Cancelled", "Failed"].includes(newState.payment_status)) {
        next.delivery_status = "";
      }
    }
    setSearchInputs(next);
    setGetListParams({
      ...paramsObject,
      ...next,
      pageNo: 0,
    });
  };
  const handleDateRangeChange = (dates) => {
    const [start, end] = dates || [null, null];
    const order_date_from = start ? moment(start).format("YYYY-MM-DD") : "";
    const order_date_to = end ? moment(end).format("YYYY-MM-DD") : "";
    const updatedSearchInputs = {
      ...searchInputs,
      order_date_from,
      order_date_to,
    };
    setSearchInputs(updatedSearchInputs);
    setGetListParams({
      ...paramsObject,
      ...updatedSearchInputs,
      pageNo: 0,
    });
  };
  const dateRangeStart = searchInputs.order_date_from
    ? new Date(searchInputs.order_date_from)
    : null;
  const dateRangeEnd = searchInputs.order_date_to
    ? new Date(searchInputs.order_date_to)
    : null;

  const { data: statusList } = useRead({
    method: "GET",
    url: "orders/payment_status_list",
    initialData: {},
  });
  const selectedOrderState = statusList?.data?.data?.find(
    (s) => String(s.status_id) === String(searchInputs?.paymentModeFilter ?? "")
  );
  const isDeliveryStatusDisabled =
    selectedOrderState &&
    ["Canceled", "Cancelled", "Failed"].includes(selectedOrderState.payment_status);

  const { setDataToServer } = useCreate({
    url: "updatepaymentstatus",
  });
  const orderReportDownload = useMutation((data) => getOrderPdf(data), {
    onSuccess: (res) => {
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.click();
    },
    onError: () => {},
  });
  const exportOrdersMutation = useMutation(
    (params) => exportOrders(params),
    {
      onSuccess: (res) => {
        const url = window.URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = "orders-export.csv";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Export downloaded");
      },
      onError: (err) => {
        toast.error(err?.message || "Export failed");
      },
    }
  );
  const handleExport = () => {
    exportOrdersMutation.mutate({ ...paramsObject });
  };
  const handleViewOrderPdf = (id) => {
    orderReportDownload.mutate({ id });
  };
  const handlePageClick = (val) => {
    setGetListParams({
      ...paramsObject,
      pageNo: val.selected,
    });
  };
  const statusColors = {
    0: "bg-red-100 text-red-600",
    1: "bg-green-100 text-green-600",
    2: "bg-yellow-100 text-yellow-600",
    3: "bg-red-100 text-red-600",
  };

  const handleChangeStatus = (status_id, order_id) => {
    setDataToServer.mutate({ order_id, status_id });
  };

  return (
    <>
      {setDataToServer.isLoading && (
        <div className="fixed w-full h-full z-50 top-0 left-0 bg-[#ffffff7d]">
          <img
            src={spinner}
            alt=""
            className="w-[10rem] h-[10rem] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          />
        </div>
      )}
      <div>
        <div className="flex items-center justify-between bg-body_bg shadow px-4 py-2">
          <h2 className="font-semibold text-3xl">Orders</h2>
          <PrimaryButton
            label={exportOrdersMutation.isPending ? "Exporting…" : "Export"}
            onClick={handleExport}
            disabled={exportOrdersMutation.isPending}
          />
        </div>
        <div className="px-4 py-3">
          <div className="space-y-3">
            {/* Filter bar */}
            <div className="bg-white rounded-lg shadow p-4 border border-gray-100">
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 items-end">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Order ID</label>
                  <input
                    name="order_id"
                    onChange={handleSearchVal}
                    onKeyDown={handleKeyDown}
                    value={searchInputs.order_id}
                    type="text"
                    className="w-full p-2 border border-gray-200 rounded text-sm"
                    placeholder="Order ID"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Customer Name</label>
                  <input
                    name="name"
                    onChange={handleSearchVal}
                    onKeyDown={handleKeyDown}
                    value={searchInputs.name}
                    type="text"
                    className="w-full p-2 border border-gray-200 rounded text-sm"
                    placeholder="Name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Phone</label>
                  <input
                    name="phone"
                    onChange={handleSearchVal}
                    onKeyDown={handleKeyDown}
                    value={searchInputs.phone}
                    type="text"
                    className="w-full p-2 border border-gray-200 rounded text-sm"
                    placeholder="Phone"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Order Date</label>
                  <ReactDatePicker
                    selectsRange
                    startDate={dateRangeStart}
                    endDate={dateRangeEnd}
                    onChange={handleDateRangeChange}
                    placeholderText="Select range"
                    className="w-full p-2 border border-gray-200 rounded text-sm"
                    dateFormat="dd/MM/yyyy"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Total Sum</label>
                  <input
                    name="total_sum"
                    onChange={handleSearchVal}
                    onKeyDown={handleKeyDown}
                    value={searchInputs.total_sum}
                    type="text"
                    className="w-full p-2 border border-gray-200 rounded text-sm"
                    placeholder="Total"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Order State</label>
                  <select
                    name="paymentModeFilter"
                    onChange={handleDropdownChange}
                    value={searchInputs?.paymentModeFilter ?? ""}
                    className="w-full p-2 border border-gray-200 rounded text-sm"
                  >
                    <option value="">All</option>
                    {statusList?.data?.data?.map((status) => (
                      <option key={status.status_id} value={status.status_id}>
                        {status.payment_status}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Delivery Status</label>
                  <select
                    name="delivery_status"
                    onChange={handleDropdownChange}
                    value={searchInputs.delivery_status ?? ""}
                    disabled={isDeliveryStatusDisabled}
                    className="w-full p-2 border border-gray-200 rounded text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <option value="">All</option>
                    <option value="delivered">Delivered</option>
                    <option value="pending">Pending</option>
                    <option value="returned">Returned</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
            </div>

            <div>
              {data.isLoading ? (
                <Spinner />
              ) : (
                <>
                  <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-100">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-24 text-center">Order ID</TableHead>
                          <TableHead className="min-w-[160px] text-center">Customer Name</TableHead>
                          <TableHead className="w-28 text-center">Phone</TableHead>
                          <TableHead className="min-w-[180px] text-center">Order Date</TableHead>
                          <TableHead className="w-28 text-center">Total Sum</TableHead>
                          <TableHead className="w-28 text-center">Order State</TableHead>
                          <TableHead className="w-28 text-center">Delivery Status</TableHead>
                          <TableHead className="w-16 text-center">View</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data?.data?.data?.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} className="text-center py-10 text-gray-500 font-medium">
                              No Data Found
                            </TableCell>
                          </TableRow>
                        ) : (
                          data?.data?.data?.map((orderData, idx) => {
                            const {
                              order_id,
                              name,
                              phone,
                              total_sum,
                              ordered_at,
                              status_id,
                              payment_status,
                              delivery_status,
                              is_cancelled,
                              is_returned,
                            } = orderData;
                            return (
                              <TableRow key={idx}>
                                <TableCell className="text-center w-24">{order_id}</TableCell>
                                <TableCell className="text-center min-w-[160px]">{name}</TableCell>
                                <TableCell className="text-center w-28">{phone}</TableCell>
                                <TableCell className="text-center min-w-[180px]">
                                  <Moment date={ordered_at} unix format="lll" />
                                </TableCell>
                                <TableCell className="text-center w-28">&#8377; {total_sum}</TableCell>
                                <TableCell className="text-center w-28">
                                  <span className={`inline-block px-2 py-1 text-xs rounded-full ${statusColors[status_id] ?? ""}`}>
                                    {payment_status}
                                  </span>
                                </TableCell>
                                <TableCell className="text-center w-28">
                                  {is_cancelled ? (
                                    <span className="inline-block px-2 py-1 text-xs rounded-full bg-red-700 text-white">Cancelled</span>
                                  ) : is_returned ? (
                                    <span className="inline-block px-2 py-1 text-xs rounded-full bg-yellow-500 text-white">Returned</span>
                                  ) : (
                                    <span
                                      className={`inline-block px-2 py-1 text-xs rounded-full ${
                                        delivery_status ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"
                                      }`}
                                    >
                                      {delivery_status ? "Delivered" : "Pending"}
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell className="text-center w-16">
                                  <Link to={`/sales/orders/${order_id}`}>
                                    <IconButton title="View Details" size="small">
                                      <RemoveRedEyeIcon fontSize="small" />
                                    </IconButton>
                                  </Link>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="my-4">
                    <Pagination
                      currentPage={paramsObject.pageNo}
                      lengthofItems={data?.data?.total_count}
                      limit={limit}
                      onPageChange={handlePageClick}
                      pageCount={Math.ceil(
                        (data?.data?.total_count || 0) / (data?.data?.per_page || 1)
                      )}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Orders;
